<template>
  <div class="content mt-consignment-payments-page">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.consignment_payment.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <!-- ปุ่มเขียน: ซ่อนทั้งปุ่มเมื่อสิทธิ์ Modern Trade เป็นอ่านอย่างเดียว -->
        <div class="col-lg-6 col-5 text-right" v-if="isEditModernTrade">
          <base-button size="sm" type="neutral" @click="openRecord()">
            <i class="fa fa-plus"></i> {{ $t("modern_trade.consignment_payment.record") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- ═══ การ์ดสรุปของบริษัทที่เลือก (ไม่เลือก = รวมทุกบริษัท) ═══ -->
      <div class="row" v-loading="summaryLoading">
        <div class="col-xl-4 col-md-6">
          <stats-card
            :title="$t('modern_trade.consignment_payment.total_received')"
            type="gradient-info"
            :sub-title="formatMoney(summary.total_received)"
            icon="ni ni-money-coins"
          >
            <template slot="footer">
              <span class="text-nowrap">{{ summaryScopeLabel }}</span>
            </template>
          </stats-card>
        </div>
        <div class="col-xl-4 col-md-6">
          <stats-card
            :title="$t('modern_trade.consignment_payment.unlinked_amount')"
            type="gradient-orange"
            :sub-title="formatMoney(summary.unlinked_amount)"
            icon="ni ni-collection"
          >
            <template slot="footer">
              <span class="text-nowrap">
                {{ $t("modern_trade.consignment_payment.last_paid_date") }}:
                {{ formatDate(summary.last_paid_date) }}
              </span>
            </template>
          </stats-card>
        </div>
        <div class="col-xl-4 col-md-6">
          <stats-card
            :title="$t('modern_trade.consignment_payment.unbilled_sales_amount')"
            type="gradient-green"
            :sub-title="formatMoney(summary.issuable_amount)"
            icon="ni ni-chart-bar-32"
          >
            <template slot="footer">
              <span class="text-nowrap text-muted">
                {{ $t("modern_trade.consignment_payment.unbilled_hint") }}
              </span>
              <!-- เพดานนี้ "หด" เมื่อมีสินค้าที่ยังไม่ผูก/ไม่มีราคา (ยอดขายของมันนับไม่ได้)
                   ต้องอธิบายตรงจุดที่โชว์ตัวเลข ไม่ใช่รอให้ออกใบไม่ผ่านก่อน -->
              <span
                v-if="summary.skipped_unpriced > 0"
                class="badge badge-warning mt-csg-badge ml-2"
                :title="$t('modern_trade.consignment_payment.skipped_unpriced_card_hint', { count: summary.skipped_unpriced })"
              >
                {{ $t("modern_trade.consignment_payment.skipped_unpriced_badge", { count: summary.skipped_unpriced }) }}
              </span>
            </template>
          </stats-card>
        </div>
      </div>

      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <h3 class="mb-0">{{ $t("modern_trade.consignment_payment.title") }}</h3>
          <p class="text-sm text-muted mb-0">
            {{ $t("modern_trade.consignment_payment.subtitle") }}
          </p>
        </template>

        <!-- ═══ ตัวกรอง ═══ -->
        <div class="row align-items-end mb-3 px-4">
          <div class="col-md-3">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.company") }}</label>
            <el-select
              v-model="filters.company_id"
              :placeholder="$t('modern_trade.consignment_payment.all_companies')"
              clearable
              class="w-100"
              @change="onFilter"
            >
              <el-option
                v-for="c in companies"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              ></el-option>
            </el-select>
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.date_from") }}</label>
            <el-date-picker
              v-model="filters.date_from"
              type="date"
              value-format="yyyy-MM-dd"
              clearable
              class="w-100"
              @change="onFilter"
            ></el-date-picker>
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.date_to") }}</label>
            <el-date-picker
              v-model="filters.date_to"
              type="date"
              value-format="yyyy-MM-dd"
              clearable
              class="w-100"
              @change="onFilter"
            ></el-date-picker>
          </div>
          <div class="col-md-3">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.link_status") }}</label>
            <el-select v-model="filters.link_status" class="w-100" @change="onFilter">
              <el-option value="" :label="$t('modern_trade.consignment_payment.status_all')"></el-option>
              <el-option value="unlinked" :label="$t('modern_trade.consignment_payment.status_unlinked')"></el-option>
              <el-option value="linked" :label="$t('modern_trade.consignment_payment.status_linked')"></el-option>
            </el-select>
          </div>
          <div class="col-md-2">
            <el-button
              type="primary"
              icon="el-icon-search"
              class="w-100 mt-csg-search-btn"
              @click="onFilter"
            >{{ $t("search") }}</el-button>
          </div>
        </div>

        <!-- spec 2026-11-03 §3B: ถอดปุ่ม "ออกใบกำกับภาษีจากรายการที่เลือก" (BE ตอบ 409
             use_sales_range_invoice แล้ว) — ใบฝากขายออกตามช่วงวันที่ขายที่หน้าใบกำกับภาษี
             แล้วกลับมาผูกเงินกับใบที่หน้านี้ (เมนูรายแถว → ผูกใบกำกับ) -->
        <div class="px-4 mb-3 mt-csg-selection-bar">
          <div class="d-flex flex-wrap align-items-center justify-content-between mt-csg-selection-figures">
            <span class="mr-3">{{ $t("modern_trade.consignment_payment.range_moved_hint") }}</span>
            <router-link :to="{ name: 'MtInvoices', query: { tab: 'consignment' } }">
              <i class="fa fa-file-invoice"></i> {{ $t("modern_trade.consignment_payment.range_moved_link") }}
            </router-link>
          </div>
        </div>

        <!-- ═══ ตารางเงินรับฝากขาย ═══ -->
        <div v-loading="loading">
          <el-table
            :data="rows"
            row-key="id"
            header-row-class-name="thead-light"
          >
            <el-table-column :label="$t('modern_trade.consignment_payment.paid_date')" min-width="110">
              <template slot-scope="{ row }">{{ formatDate(row.paid_date) }}</template>
            </el-table-column>
            <!-- เงินเก่าที่ company_id เป็น NULL: BE ตั้งใจโชว์ไว้ให้เห็นเพื่อไปแก้
                 (ผูกใบก็ไม่ได้ ออกใบก็ไม่ได้) — ต้องบอกผู้ใช้ว่าทำไมติ๊กไม่ได้ -->
            <el-table-column :label="$t('modern_trade.consignment_payment.company')" min-width="180">
              <template slot-scope="{ row }">
                <template v-if="row.company_id">{{ row.company_name || row.company_id }}</template>
                <template v-else>
                  <span class="text-muted">{{ $t("modern_trade.consignment_payment.no_company") }}</span>
                  <small class="d-block text-muted">
                    {{ $t("modern_trade.consignment_payment.no_company_hint") }}
                  </small>
                </template>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.amount')" min-width="130" align="right">
              <template slot-scope="{ row }">{{ formatMoney(row.amount) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.method')" min-width="110">
              <template slot-scope="{ row }">{{ methodLabel(row.method) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.slip')" min-width="80" align="center">
              <template slot-scope="{ row }">
                <a v-if="row.slip_s3_key" :href="slipUrl(row.slip_s3_key)" target="_blank" rel="noopener">
                  <i class="fa fa-paperclip"></i>
                </a>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.invoice')" min-width="150">
              <template slot-scope="{ row }">
                <span v-if="row.invoice_id" class="badge badge-success mt-csg-badge">
                  {{ row.invoice_doc_no || "#" + row.invoice_id }}
                </span>
                <span v-else class="badge badge-warning mt-csg-badge">
                  {{ $t("modern_trade.consignment_payment.pending_invoice") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.note')" min-width="150">
              <template slot-scope="{ row }">{{ row.note || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.created_by')" min-width="130">
              <template slot-scope="{ row }">{{ row.created_by_name || "-" }}</template>
            </el-table-column>
            <!-- ทั้งคอลัมน์หายไปเมื่อผู้ใช้มีสิทธิ์อ่านอย่างเดียว -->
            <el-table-column
              v-if="isEditModernTrade"
              :label="$t('actions')"
              width="90"
              align="center"
            >
              <template slot-scope="{ row }">
                <el-dropdown trigger="click" @command="cmd => onRowCommand(cmd, row)">
                  <base-button size="sm" type="secondary" class="btn-icon-only" :title="$t('actions')">
                    <i class="fa fa-ellipsis-v"></i>
                  </base-button>
                  <el-dropdown-menu slot="dropdown">
                    <!-- แถวไร้บริษัท: MtPaymentLink ตอบ 409 payment_no_company เสมอ
                         → ปิดปุ่มพร้อมบอกเหตุผล ไม่ปล่อยให้กดแล้วไปตายที่ BE -->
                    <el-dropdown-item command="link" :disabled="!!row.invoice_id || !row.company_id">
                      <i class="fa fa-link text-info mr-2"></i>{{ $t("modern_trade.consignment_payment.link") }}
                      <small v-if="!row.company_id" class="d-block text-muted">
                        {{ $t("modern_trade.consignment_payment.no_company_short") }}
                      </small>
                    </el-dropdown-item>
                    <el-dropdown-item command="unlink" :disabled="!row.invoice_id">
                      <i class="fa fa-unlink text-warning mr-2"></i>{{ $t("modern_trade.consignment_payment.unlink") }}
                    </el-dropdown-item>
                    <el-dropdown-item command="delete" divided>
                      <span class="text-danger"><i class="fa fa-trash mr-2"></i>{{ $t("delete") }}</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>
          </el-table>
          <p v-if="!loading && rows.length === 0" class="text-muted text-center py-4 mb-0">
            {{ $t("modern_trade.consignment_payment.no_data") }}
          </p>
        </div>

        <div class="d-flex align-items-center justify-content-between mt-3 px-4">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
          <base-pagination
            v-model="page"
            :per-page="perPage"
            :total="total"
            @input="loadList"
          ></base-pagination>
        </div>
      </card>
    </div>

    <!-- ═══ Dialog: บันทึกรับเงิน (ไม่มีเพดาน — LOCKED) ═══ -->
    <el-dialog
      :title="$t('modern_trade.consignment_payment.record_title')"
      :visible.sync="showRecordDialog"
      width="640px"
      @closed="resetRecordForm"
    >
      <div class="alert alert-info py-2">
        <i class="fa fa-info-circle"></i> {{ $t("modern_trade.consignment_payment.no_cap_hint") }}
      </div>
      <div class="row">
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.company") }} *</label>
            <el-select
              v-model="recordForm.company_id"
              :placeholder="$t('modern_trade.consignment_payment.company')"
              filterable
              class="w-100"
              @change="onRecordCompanyChange"
            >
              <el-option
                v-for="c in companies"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              ></el-option>
            </el-select>
          </div>
        </div>
        <!-- ตัวเลขเทียบให้เห็น (ไม่ใช่ guard) -->
        <div class="col-12" v-if="recordForm.company_id">
          <div class="row mb-3">
            <div class="col-6">
              <small class="text-muted d-block">{{ $t("modern_trade.consignment_payment.unlinked_amount") }}</small>
              <strong>{{ formatMoney(recordCompare.unlinked_amount) }}</strong>
            </div>
            <div class="col-6 text-right">
              <small class="text-muted d-block">{{ $t("modern_trade.consignment_payment.unbilled_sales_amount") }}</small>
              <strong>{{ formatMoney(recordCompare.issuable_amount) }}</strong>
            </div>
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.amount") }} *</label>
            <input
              v-model.number="recordForm.amount"
              class="form-control"
              type="number"
              min="0"
              step="0.01"
              @focus="$event.target.select()"
            />
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.paid_date") }} *</label>
            <el-date-picker
              v-model="recordForm.paid_date"
              type="date"
              value-format="yyyy-MM-dd"
              class="w-100"
            ></el-date-picker>
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.method") }}</label>
            <el-select v-model="recordForm.method" class="w-100">
              <el-option value="cash" :label="$t('modern_trade.payment.method_cash')"></el-option>
              <el-option value="transfer" :label="$t('modern_trade.payment.method_transfer')"></el-option>
              <el-option value="cheque" :label="$t('modern_trade.payment.method_cheque')"></el-option>
              <el-option value="other" :label="$t('modern_trade.payment.method_other')"></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.note") }}</label>
            <input v-model="recordForm.note" class="form-control" type="text" />
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.slip") }}</label>
            <div>
              <input
                ref="slipInput"
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                style="display:none"
                @change="onSlipPicked"
              />
              <el-button size="small" :loading="slipUploading" @click="$refs.slipInput.click()">
                <i class="fa fa-upload"></i> {{ $t("modern_trade.payment.slip_choose") }}
              </el-button>
              <span v-if="recordForm.slip_object_key" class="ml-2">
                <a :href="recordForm.slip_cdn_url" target="_blank" rel="noopener">
                  <i class="fa fa-paperclip"></i> {{ $t("modern_trade.payment.slip_view") }}
                </a>
                <i class="fa fa-times text-danger ml-2" style="cursor:pointer" @click="clearSlip"></i>
              </span>
            </div>
            <small class="text-muted d-block mt-1">{{ $t("modern_trade.payment.slip_hint") }}</small>
          </div>
        </div>
        <!-- §3B: ตอนบันทึกเงินใหม่ เลือกใบที่จะผูกได้ทันที (ไม่บังคับ) -->
        <div class="col-12" v-if="recordForm.company_id">
          <div class="form-group mb-0" v-loading="recordInvoicesLoading">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.record_invoice") }}</label>
            <el-select
              v-model="recordInvoiceId"
              :placeholder="$t('modern_trade.consignment_payment.record_invoice_none')"
              clearable
              class="w-100"
            >
              <el-option
                v-for="iv in recordInvoices"
                :key="iv.id"
                :label="invoiceOptionLabel(iv)"
                :value="iv.id"
              ></el-option>
            </el-select>
            <small v-if="recordSelectedInvoice" class="text-muted d-block mt-1">
              {{
                $t("modern_trade.consignment_payment.record_invoice_hint", {
                  linked: formatMoney(recordLinkAmount),
                  remainder: formatMoney(recordLinkRemainder),
                })
              }}
            </small>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showRecordDialog = false">{{ $t("close") }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitRecord">{{ $t("save") }}</el-button>
      </span>
    </el-dialog>

    <!-- ═══ Dialog: ผูกใบกำกับภาษี ═══ -->
    <el-dialog
      :title="$t('modern_trade.consignment_payment.link_title')"
      :visible.sync="showLinkDialog"
      width="640px"
      @closed="resetLinkForm"
    >
      <div v-if="linkPayment">
        <div class="row mb-3">
          <div class="col-6">
            <small class="text-muted d-block">{{ $t("modern_trade.consignment_payment.company") }}</small>
            <strong>{{ linkPayment.company_name || linkPayment.company_id }}</strong>
          </div>
          <div class="col-6 text-right">
            <small class="text-muted d-block">{{ $t("modern_trade.consignment_payment.amount") }}</small>
            <strong class="text-success">{{ formatMoney(linkPayment.amount) }}</strong>
          </div>
        </div>
        <p class="text-muted small">{{ $t("modern_trade.consignment_payment.link_hint") }}</p>
        <div v-loading="linkLoading">
          <template v-if="linkInvoices.length">
            <el-select
              v-model="linkInvoiceId"
              :placeholder="$t('modern_trade.consignment_payment.invoice')"
              class="w-100"
              @change="onLinkInvoiceChange"
            >
              <el-option
                v-for="iv in linkInvoices"
                :key="iv.id"
                :label="invoiceOptionLabel(iv)"
                :value="iv.id"
              ></el-option>
            </el-select>
            <!-- §3B: ผูกบางส่วนได้ — ส่วนที่เหลือ BE แตกเป็นแถวใหม่ที่ยังรอผูก -->
            <div v-if="linkInvoiceId" class="form-group mt-3 mb-0">
              <label class="form-control-label">{{ $t("modern_trade.consignment_payment.link_amount_input") }} *</label>
              <input
                v-model.number="linkAmount"
                class="form-control"
                type="number"
                min="0"
                step="0.01"
                @focus="$event.target.select()"
              />
              <small class="d-block mt-1" :class="linkAmountErrorKey ? 'text-danger' : 'text-muted'">
                {{
                  linkAmountErrorKey
                    ? linkAmountErrorText(linkAmountErrorKey)
                    : $t("modern_trade.consignment_payment.link_amount_hint", { remainder: formatMoney(linkRemainder) })
                }}
              </small>
            </div>
          </template>
          <p v-else-if="!linkLoading" class="text-muted mb-0">
            {{ $t("modern_trade.consignment_payment.link_none") }}
          </p>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showLinkDialog = false">{{ $t("close") }}</el-button>
        <el-button
          type="primary"
          :loading="linking"
          :disabled="!linkInvoiceId || !!linkAmountErrorKey"
          @click="submitLink"
        >{{ $t("modern_trade.consignment_payment.link") }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
/**
 * รับเงินฝากขาย — spec docs/specs/2026-08-13-consignment-invoice-from-money.md
 *
 * ความจริงของธุรกิจ: คู่ค้าโอนเงินมาก่อนตามยอดที่ขายได้ → บันทึกไว้ผูกกับ "บริษัท"
 * (sale_order_id NULL + invoice_id NULL = กองรอออกใบ)
 *
 * spec 2026-11-03 §3B: ใบกำกับฝากขายออกตาม "ช่วงวันที่ขาย" ที่หน้าใบกำกับภาษี
 * (แท็บยอดขายฝากขายที่ยังไม่ออกใบ) — การออกใบจากเงินที่เลือกถูกถอดแล้ว
 * (BE ตอบ 409 use_sales_range_invoice). หน้านี้เหลือหน้าที่บันทึกรับเงิน และผูก
 * เงินเข้ากับใบ (ผูกบางส่วนได้ — BE แตกแถว ส่วนที่เหลือกลับเข้ากองรอผูก)
 *
 * หน้านี้ไม่มีเพดานตอนบันทึกรับเงิน (LOCKED) — การ์ด "ยอดขายที่ยังออกใบได้"
 * เป็นตัวเลขเทียบ (เพดานจริงตัดสินที่ BE ตอนออกใบ)
 */
import { Table, TableColumn, Select, Option, Dialog, Button, DatePicker, Dropdown, DropdownMenu, DropdownItem } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination, StatsCard } from "@/components";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";
import { invoiceOutstanding, defaultLinkAmount, linkAmountError, linkPayload, round2 } from "@/util/mt/invoiceUi";

export default {
  name: "MtConsignmentPayments",
  components: {
    RouteBreadCrumb,
    BasePagination,
    StatsCard,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [DatePicker.name]: DatePicker,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    // เลือกบริษัท = การ์ดของบริษัทนั้น / ไม่เลือก = รวมทุกบริษัทที่มีความเคลื่อนไหว
    // issuable_amount = ตัวเลขเดียวที่หน้าจอโชว์เป็น "ยอดขายที่ยังออกใบได้" (รวม VAT)
    summary() {
      const empty = {
        total_received: 0,
        unlinked_amount: 0,
        issuable_amount: 0,
        skipped_unpriced: 0,
        last_paid_date: null,
      };
      if (!this.summaryRows.length) return empty;
      if (this.filters.company_id) {
        const row = this.summaryRows.find(s => Number(s.company_id) === Number(this.filters.company_id));
        if (!row) return empty;
        return Object.assign({}, row, {
          issuable_amount: this.issuableAmount(row),
          skipped_unpriced: Number(row.skipped_unpriced) || 0,
        });
      }
      return this.summaryRows.reduce((acc, s) => {
        acc.total_received += Number(s.total_received) || 0;
        acc.unlinked_amount += Number(s.unlinked_amount) || 0;
        acc.issuable_amount += this.issuableAmount(s);
        acc.skipped_unpriced += Number(s.skipped_unpriced) || 0;
        if (s.last_paid_date && (!acc.last_paid_date || s.last_paid_date > acc.last_paid_date)) {
          acc.last_paid_date = s.last_paid_date;
        }
        return acc;
      }, Object.assign({}, empty));
    },
    summaryScopeLabel() {
      if (!this.filters.company_id) return this.$t("modern_trade.consignment_payment.summary_all_hint");
      const c = this.companies.find(x => Number(x.id) === Number(this.filters.company_id));
      return (c && c.name) || this.$t("modern_trade.consignment_payment.company");
    },
    // ── ผูกใบกำกับ (§3B: ผูกบางส่วนได้) ──────────────────────────────────────
    linkSelectedInvoice() {
      return this.linkInvoices.find(iv => iv.id === this.linkInvoiceId) || null;
    },
    linkAmountErrorKey() {
      if (!this.linkPayment || !this.linkSelectedInvoice) return null;
      return linkAmountError(this.linkAmount, this.linkPayment.amount, invoiceOutstanding(this.linkSelectedInvoice));
    },
    // ส่วนที่จะแตกออกเป็นแถวใหม่ที่ยังรอผูก
    linkRemainder() {
      if (!this.linkPayment) return 0;
      return Math.max(round2((Number(this.linkPayment.amount) || 0) - (Number(this.linkAmount) || 0)), 0);
    },
    recordSelectedInvoice() {
      return this.recordInvoices.find(iv => iv.id === this.recordInvoiceId) || null;
    },
    // ผูกได้ไม่เกินยอดค้างรับของใบ — ส่วนเกิน BE แตกเป็นแถวใหม่ที่ยังรอผูก
    recordLinkAmount() {
      if (!this.recordSelectedInvoice) return 0;
      return defaultLinkAmount(this.recordForm.amount, invoiceOutstanding(this.recordSelectedInvoice));
    },
    recordLinkRemainder() {
      return Math.max(round2((Number(this.recordForm.amount) || 0) - this.recordLinkAmount), 0);
    },
    // ตัวเลขเทียบใน dialog บันทึกรับเงิน (ของบริษัทที่เลือกใน dialog)
    recordCompare() {
      const empty = { unlinked_amount: 0, issuable_amount: 0 };
      if (!this.recordForm.company_id) return empty;
      const row = this.summaryRows.find(s => Number(s.company_id) === Number(this.recordForm.company_id));
      if (!row) return empty;
      return Object.assign({}, row, { issuable_amount: this.issuableAmount(row) });
    },
  },
  data() {
    return {
      companies: [],
      loading: false,
      rows: [],
      total: 0,
      page: 1,
      perPage: 20,
      filters: {
        company_id: null,
        date_from: null,
        date_to: null,
        link_status: "",
      },
      summaryLoading: false,
      summaryRows: [],
      // บันทึกรับเงิน
      showRecordDialog: false,
      saving: false,
      slipUploading: false,
      recordForm: {
        company_id: null,
        amount: null,
        paid_date: null,
        method: "transfer",
        note: "",
        slip_object_key: "",
        slip_cdn_url: "",
      },
      recordInvoices: [],
      recordInvoiceId: null,
      recordInvoicesLoading: false,
      recordInvoicesSeq: 0,
      // ผูกใบกำกับ
      showLinkDialog: false,
      linkPayment: null,
      linkInvoices: [],
      linkInvoiceId: null,
      linkAmount: null,
      linkLoading: false,
      linking: false,
    };
  },
  mounted() {
    this.loadCompanies();
    this.loadSummary();
    this.loadList();
  },
  methods: {
    // BE ห่อผลลัพธ์เป็น { Data: [ { total, items } ] } (r.Append ของ object)
    // แต่บาง endpoint append ทีละแถว → Data เป็นอาเรย์ของแถวตรง ๆ. รองรับทั้งสองแบบ
    parseList(res) {
      const body = (res && res.data) || {};
      const arr = Array.isArray(body.Data) ? body.Data : [];
      const first = arr[0] || {};
      if (first && Array.isArray(first.items)) {
        return { items: first.items, total: Number(first.total) || first.items.length };
      }
      if (first && Array.isArray(first.data)) {
        return { items: first.data, total: Number(first.total) || first.data.length };
      }
      return { items: arr, total: Number(body.Total) || arr.length };
    },
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ per_page: 200, status: 1 });
        const d = this.parseList(res);
        this.companies = d.items || [];
      } catch (e) {
        // non-fatal
      }
    },
    async loadSummary() {
      this.summaryLoading = true;
      try {
        const params = {};
        if (this.filters.company_id) params.company_id = this.filters.company_id;
        const res = await ModernTradeService.consignmentPaymentsSummary(params);
        const d = this.parseList(res);
        this.summaryRows = d.items || [];
      } catch (err) {
        this.summaryRows = [];
      } finally {
        this.summaryLoading = false;
      }
    },
    onFilter() {
      this.page = 1;
      this.loadSummary();
      this.loadList();
    },
    async loadList() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.filters.company_id) params.company_id = this.filters.company_id;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
        if (this.filters.link_status) params.link_status = this.filters.link_status;
        const res = await ModernTradeService.consignmentPaymentsList(params);
        const d = this.parseList(res);
        this.rows = d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loading = false;
      }
    },

    // "ยอดขายที่ยังออกใบได้" ของแถว summary หนึ่งบริษัท
    //
    // BE ส่งมา 2 ตัวจากฐานเดียวกัน: unbilled_sales_amount = ฐานภาษี (ไม่รวม VAT)
    // ส่วน issuable_amount_incl_vat = ตัวเดียวกันบวก VAT กลับด้วยอัตราของบริษัทนั้น
    // หน้าจอนี้เทียบกับ "เงินที่คู่ค้าโอนมา" ซึ่งเป็นยอดรวม VAT จึงต้องใช้ตัวหลัง
    // (ตัวเดียวกับ max_amount ที่ insufficient_sales ตอบกลับมา — เลขก่อน/หลังโดนปฏิเสธ
    // จะได้ตรงกัน) — ใช้ตัวไม่รวม VAT จะเตือน "เกินยอด" ผิดตั้งแต่ยังไม่เกินจริง
    //
    // fallback ไปตัวไม่รวม VAT เฉพาะกรณี API รุ่นก่อนหน้า (ช่วง deploy คาบเกี่ยว)
    issuableAmount(row) {
      if (!row) return 0;
      const v = row.issuable_amount_incl_vat;
      if (v !== undefined && v !== null && v !== "" && isFinite(Number(v))) return Number(v);
      return Number(row.unbilled_sales_amount) || 0;
    },

    // ดึงสรุปของบริษัทเดียวมาเติม (ตัวเลขเทียบใน dialog บันทึกรับเงิน)
    async fetchCompanySummary(companyId) {
      try {
        const res = await ModernTradeService.consignmentPaymentsSummary({ company_id: companyId });
        const d = this.parseList(res);
        const rows = (d.items || []).filter(
          s => !this.summaryRows.some(x => Number(x.company_id) === Number(s.company_id))
        );
        if (rows.length) this.summaryRows = this.summaryRows.concat(rows);
      } catch (e) {
        // non-fatal — ตัวเลขเทียบไม่ขึ้นก็ยังกดออกใบได้ (BE เป็นคนตัดสิน)
      }
    },
    // ── บันทึกรับเงิน ───────────────────────────────────────────────────────────
    openRecord() {
      if (!this.isEditModernTrade) return;
      this.recordForm.company_id = this.filters.company_id || null;
      this.recordForm.paid_date = this.today();
      this.showRecordDialog = true;
      this.loadRecordInvoices();
    },
    resetRecordForm() {
      this.recordForm = {
        company_id: null,
        amount: null,
        paid_date: null,
        method: "transfer",
        note: "",
        slip_object_key: "",
        slip_cdn_url: "",
      };
      if (this.$refs.slipInput) this.$refs.slipInput.value = "";
      this.recordInvoices = [];
      this.recordInvoiceId = null;
      this.recordInvoicesSeq++;
      this.recordInvoicesLoading = false;
    },
    onRecordCompanyChange() {
      this.loadRecordCompare();
      this.loadRecordInvoices();
    },
    // ใบที่ผูกได้ของบริษัทใน dialog (โหลดแบบเดียวกับ openLink). ตัวนับกันคำตอบเก่า
    // ของบริษัทก่อนหน้ามาทับเมื่อสลับบริษัทเร็ว ๆ
    async loadRecordInvoices() {
      const companyId = this.recordForm.company_id;
      const seq = ++this.recordInvoicesSeq;
      this.recordInvoices = [];
      this.recordInvoiceId = null;
      if (!companyId) {
        this.recordInvoicesLoading = false;
        return;
      }
      this.recordInvoicesLoading = true;
      try {
        const list = await this.fetchLinkableInvoices(companyId);
        if (seq !== this.recordInvoicesSeq) return;
        this.recordInvoices = list;
      } catch (err) {
        // non-fatal — บันทึกเงินได้โดยไม่ผูกใบ
        if (seq === this.recordInvoicesSeq) this.recordInvoices = [];
      } finally {
        if (seq === this.recordInvoicesSeq) this.recordInvoicesLoading = false;
      }
    },
    // ตัวเลขเทียบของบริษัทใน dialog — ถ้ายังไม่มีใน summaryRows (เพราะกรองบริษัทอื่นอยู่)
    // ให้ดึงเฉพาะบริษัทนั้นมาเติม
    async loadRecordCompare() {
      const id = this.recordForm.company_id;
      if (!id) return;
      if (this.summaryRows.some(s => Number(s.company_id) === Number(id))) return;
      // non-fatal — การ์ดเทียบไม่ขึ้นก็บันทึกได้ (ไม่มีเพดาน)
      await this.fetchCompanySummary(id);
    },
    async submitRecord() {
      if (!this.isEditModernTrade) return;
      if (!this.recordForm.company_id) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.company_required"), icon: "warning" });
        return;
      }
      const amount = Number(this.recordForm.amount);
      if (!amount || !isFinite(amount) || amount <= 0) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.amount_required"), icon: "warning" });
        return;
      }
      if (!this.recordForm.paid_date) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.date_required"), icon: "warning" });
        return;
      }
      this.saving = true;
      const linkInvoiceId = this.recordSelectedInvoice ? this.recordInvoiceId : null;
      const linkAmount = this.recordLinkAmount;
      try {
        const res = await ModernTradeService.consignmentPaymentCreate({
          company_id: this.recordForm.company_id,
          amount: amount,
          paid_date: this.recordForm.paid_date,
          method: this.recordForm.method,
          note: this.recordForm.note,
          slip_object_key: this.recordForm.slip_object_key || null,
        });
        // ผูกใบทันที (ถ้าเลือก) — เงินบันทึกแล้วไม่ว่าผูกสำเร็จหรือไม่
        const created = (res && res.data && Array.isArray(res.data.Data) && res.data.Data[0]) || {};
        if (linkInvoiceId && Number(created.id) > 0 && linkAmount > 0) {
          try {
            await ModernTradeService.paymentLink(created.id, linkPayload(linkInvoiceId, linkAmount, amount));
          } catch (linkErr) {
            this.showRecordDialog = false;
            this.loadSummary();
            this.loadList();
            swal.fire({
              title: this.$t("modern_trade.consignment_payment.record_link_failed"),
              text: this.errMsg(linkErr),
              icon: "warning",
            });
            return;
          }
        }
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.recorded"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.showRecordDialog = false;
        this.loadSummary();
        this.loadList();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.saving = false;
      }
    },

    // ── สลิป (presign เดิม object_type = mt_payment) ────────────────────────────
    clearSlip() {
      this.recordForm.slip_object_key = "";
      this.recordForm.slip_cdn_url = "";
      if (this.$refs.slipInput) this.$refs.slipInput.value = "";
    },
    slipUrl(key) {
      if (!key) return "";
      if (/^https?:\/\//i.test(key)) return key;
      return "https://cdn.madamefin.com/" + String(key).replace(/^\/+/, "");
    },
    async onSlipPicked(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      const okType = ["image/jpeg", "image/png", "image/webp", "application/pdf"].indexOf(file.type) !== -1;
      if (!okType) {
        swal.fire({ title: this.$t("modern_trade.payment.slip_bad_type"), icon: "warning" });
        this.clearSlip();
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        swal.fire({ title: this.$t("modern_trade.payment.slip_too_big"), icon: "warning" });
        this.clearSlip();
        return;
      }
      this.slipUploading = true;
      try {
        const presignRes = await ModernTradeService.slipPresign({
          file_name: file.name,
          content_type: file.type,
        });
        const d = (presignRes.data && presignRes.data.Data && presignRes.data.Data[0]) || {};
        if (!d.upload_url || !d.object_key) throw new Error("presign failed");
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", d.upload_url);
          xhr.setRequestHeader("Content-Type", file.type);
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error("Upload failed: " + xhr.status));
          };
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.send(file);
        });
        this.recordForm.slip_object_key = d.object_key;
        this.recordForm.slip_cdn_url = d.cdn_url || this.slipUrl(d.object_key);
      } catch (err) {
        const detail = err && err.message ? ": " + err.message : "";
        swal.fire({ title: this.$t("modern_trade.payment.slip_upload_failed") + detail, icon: "error" });
        this.clearSlip();
      } finally {
        this.slipUploading = false;
      }
    },

    // ── เมนูรายแถว (ผูก / ปลด / ลบ) — ทั้งคอลัมน์ซ่อนเมื่อไม่มีสิทธิ์เขียน ──────
    onRowCommand(cmd, row) {
      if (!this.isEditModernTrade) return;
      // ด่านสองของปุ่มผูกใบ (เมนูปิดไว้แล้ว) — ผูกไม่ได้จริงเพราะไม่มีบริษัท
      if (cmd === "link" && !row.company_id) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.no_company_hint"), icon: "warning" });
        return;
      }
      if (cmd === "link") this.openLink(row);
      else if (cmd === "unlink") this.handleUnlink(row);
      else if (cmd === "delete") this.handleDelete(row);
    },
    // โหลดใบกำกับที่ผูกได้ตอนเปิด dialog — el-select remote ไม่ยิงตอนกางรายการ
    // (Element UI 2.4.11) จึง prefetch ของบริษัทนั้นแทน
    //
    // MtInvoiceList (MT-Invoice.go:504) clamp per_page > 100 กลับเป็น 50 — ขอ 200
    // จึงได้ 50 แถวล่าสุด ซึ่งอาจเป็นใบขายขาดล้วนจนกรองแล้วเหลือศูนย์ทั้งที่มีใบฝากขาย
    // ค้างอยู่จริง. ใช้ค่าสูงสุดที่ BE ยอม (100) + ยิงแยกตามสถานะที่ผูกได้
    // (1 ยังไม่รับเงิน / 2 รับบางส่วน) เพื่อให้ทั้ง 2 หน้าเป็นใบที่ยังรับเงินไม่ครบล้วน
    // — BE ไม่มีตัวกรอง source จึงยังต้องกรอง source=2 ฝั่ง FE
    async openLink(row) {
      this.linkPayment = row;
      this.linkInvoiceId = null;
      this.linkAmount = null;
      this.linkInvoices = [];
      this.showLinkDialog = true;
      this.linkLoading = true;
      try {
        this.linkInvoices = await this.fetchLinkableInvoices(row.company_id);
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.linkLoading = false;
      }
    },
    async fetchLinkableInvoices(companyId) {
      const base = { company_id: companyId, per_page: 100, page: 1 };
      const [unpaidRes, partialRes] = await Promise.all([
        ModernTradeService.invoicesList(Object.assign({ status: 1 }, base)),
        ModernTradeService.invoicesList(Object.assign({ status: 2 }, base)),
      ]);
      const items = (this.parseList(unpaidRes).items || []).concat(this.parseList(partialRes).items || []);
      // ผูกได้เฉพาะใบฝากขาย (source 2) ที่ยังไม่ยกเลิกและยังรับเงินไม่ครบ
      return items
        .filter(iv => iv.source === 2 && iv.status !== 9 && this.invoiceRemaining(iv) > 0)
        .sort((a, b) => Number(b.id) - Number(a.id));
    },
    resetLinkForm() {
      this.linkPayment = null;
      this.linkInvoices = [];
      this.linkInvoiceId = null;
      this.linkAmount = null;
    },
    // เลือกใบ = ตั้งยอดเริ่มต้น min(เงินรายการนี้, ยอดค้างรับของใบ)
    onLinkInvoiceChange() {
      const iv = this.linkSelectedInvoice;
      this.linkAmount = iv && this.linkPayment ? defaultLinkAmount(this.linkPayment.amount, invoiceOutstanding(iv)) : null;
    },
    invoiceRemaining(iv) {
      return invoiceOutstanding(iv);
    },
    // คีย์เต็มเพื่อให้ grep/เครื่องมือตรวจ i18n เจอ (ห้ามต่อสตริงคีย์)
    linkAmountErrorText(key) {
      switch (key) {
        case "link_amount_required": return this.$t("modern_trade.consignment_payment.link_amount_required");
        case "link_amount_over_payment": return this.$t("modern_trade.consignment_payment.link_amount_over_payment");
        case "link_amount_over_outstanding": return this.$t("modern_trade.consignment_payment.link_amount_over_outstanding");
        default: return "";
      }
    },
    invoiceOptionLabel(iv) {
      return `${iv.doc_no || "#" + iv.id} — ${this.$t("modern_trade.consignment_payment.invoice_remaining")} ${this.formatMoney(this.invoiceRemaining(iv))}`;
    },
    async submitLink() {
      if (!this.isEditModernTrade || !this.linkPayment) return;
      if (!this.linkInvoiceId) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.invoice_select_required"), icon: "warning" });
        return;
      }
      if (this.linkAmountErrorKey) {
        swal.fire({ title: this.linkAmountErrorText(this.linkAmountErrorKey), icon: "warning" });
        return;
      }
      this.linking = true;
      try {
        await ModernTradeService.paymentLink(
          this.linkPayment.id,
          linkPayload(this.linkInvoiceId, this.linkAmount, this.linkPayment.amount)
        );
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.linked"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.showLinkDialog = false;
        this.loadSummary();
        this.loadList();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.linking = false;
      }
    },
    async handleUnlink(row) {
      const result = await swal.fire({
        title: this.$t("modern_trade.consignment_payment.unlink_confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("close") || "Cancel",
      });
      // sweetalert2 v9 → result.value
      if (!result.value) return;
      try {
        await ModernTradeService.paymentLink(row.id, { invoice_id: null });
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.unlinked"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.loadSummary();
        this.loadList();
      } catch (err) {
        this.showErr(err);
      }
    },
    async handleDelete(row) {
      const result = await swal.fire({
        title: this.$t("modern_trade.consignment_payment.delete_confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("close") || "Cancel",
      });
      if (!result.value) return;
      try {
        await ModernTradeService.paymentDelete(row.id);
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.deleted"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.loadSummary();
        this.loadList();
      } catch (err) {
        this.showErr(err);
      }
    },

    // ── utils ───────────────────────────────────────────────────────────────────
    // BE ส่งโค้ดข้อผิดพลาดใน Msg (payment_exceeds / wrong_payment_type) — แปลให้อ่านออก
    showErr(err) {
      const code = (err && err.response && err.response.data && err.response.data.Msg) || "";
      let title = this.errMsg(err);
      if (code === "payment_exceeds") title = this.$t("modern_trade.consignment_payment.exceeds");
      else if (code === "wrong_payment_type") title = this.$t("modern_trade.consignment_payment.wrong_type");
      // เงินเก่าที่ไม่มีบริษัท (เมนูปิดไว้แล้ว เหลือกรณี race/ยิงตรง) — Cause ของ BE
      // เป็นไทยล้วน จึงต้องมีข้อความของหน้าจอเอง
      else if (code === "payment_no_company") title = this.$t("modern_trade.consignment_payment.no_company_hint");
      // แถวถูกแก้ไปแล้วระหว่างที่หน้าจอยังถืออันเก่า — BE ตั้งใจให้ client ลองใหม่
      // (ไม่ใช่ edge case) ข้อความจึงต้องบอก "ต้องทำอะไรต่อ" ไม่ใช่บอกว่าเกิดอะไรขึ้น
      else if (code === "state_changed") title = this.$t("modern_trade.consignment_payment.state_changed");
      swal.fire({ title, icon: "error" });
    },
    methodLabel(m) {
      switch (m) {
        case "cash": return this.$t("modern_trade.payment.method_cash");
        case "transfer": return this.$t("modern_trade.payment.method_transfer");
        case "cheque": return this.$t("modern_trade.payment.method_cheque");
        case "other": return this.$t("modern_trade.payment.method_other");
        default: return m || "-";
      }
    },
    today() {
      const d = new Date();
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    errMsg(err) {
      return (err && err.response && err.response.data && err.response.data.Cause) || (err && err.message) || "Error";
    },
  },
};
</script>

<style scoped>
/* ปุ่มค้นหาสูงเท่า el-input/el-select (40px) เหมือนหน้าใบกำกับภาษี */
.mt-csg-search-btn {
  height: 40px;
}

/* แถบสรุปการเลือก — แยกตัวเองออกจากตัวกรองด้านบนและตารางด้านล่าง */
.mt-csg-selection-bar {
  padding-top: 0.75rem;
  padding-bottom: 0.5rem;
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fe;
}

.mt-csg-selection-figures {
  font-size: 0.875rem;
}

/* Argon .badge = font-size 66% + uppercase → เล็กเกินอ่านไม่ออกบนตาราง */
.mt-csg-badge {
  display: inline-block;
  padding: 0.35rem 0.6rem;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.2;
  text-transform: none;
  white-space: nowrap;
  border-radius: 0.375rem;
}
</style>
